import { useSymbolSelectorWidth } from '@src/components/SymbolSelector/useSymbolSelectorWidth';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import {
  Box,
  DepthTypeEnum,
  Divider,
  EMPTY_ARRAY,
  FeeModeEnum,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  InlineFormattedNumber,
  Input,
  isMultileg,
  LiquidityTypeEnum,
  MARKET_DATA_SNAPSHOT,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  SymbolSelector,
  SymbolSelectorAppearance,
  Text,
  useMarketDataStatistics,
  useMixpanel,
  useObservableValue,
  useSecuritiesContext,
  useSubscription,
  VStack,
  type MarketDataSnapshot,
} from '@talos/kyoko';
import { memo, useCallback, useMemo } from 'react';
import { map } from 'rxjs/operators';
import { useTheme } from 'styled-components';
import { useCloneMultilegSymbol } from '../../hooks/useCloneMultilegSymbol';
import { useIsPricingModeBPS } from '../../hooks/useIsPricingModeBPS';
import { useNavigateToMultilegCombo } from '../../hooks/useNavigateToMultilegCombo';
import { getMarketDataLevelPrices } from '../../utils/getMarketDataLevelPrice';
import { SymbolActionsMenu } from '../SymbolActionsMenu';
import {
  selectEditableProperties,
  selectIsEditing,
  setResolvedSymbol,
  updateEditableProperty,
} from './MultilegComboSlice';
import { isMultilegOptionComboType, type MultilegComboType } from './enums';

export interface MultilegComboHeaderProps {
  instrumentType: MultilegComboType | undefined;
  symbol: string | undefined;
  panelID: string;
  legMarketAccounts?: string[];
  legMarkets?: string[];
}

export const MultilegComboHeader = memo(
  ({ symbol, instrumentType, panelID, legMarketAccounts = [], legMarkets = [] }: MultilegComboHeaderProps) => {
    const mixpanel = useMixpanel();
    const { securitiesBySymbol, searchableSecurities } = useSecuritiesContext();
    const { showAllInPrices, showFirmLiquidity } = useDisplaySettings();
    const theme = useTheme();
    const navigateToMultilegCombo = useNavigateToMultilegCombo();
    const cloneMultilegSymbol = useCloneMultilegSymbol(MixpanelEventSource.MultilegDeepDive);

    const isEditing = useAppStateSelector((state: AppState) => selectIsEditing(state, panelID));
    const security = symbol ? securitiesBySymbol.get(symbol) : undefined;

    const handleCreateMultileg = useCallback(
      (multilegType: MultilegComboType) => {
        if (!security) {
          return;
        }
        mixpanel.track(MixpanelEvent.CreateNewMultileg, {
          [MixpanelEventProperty.Type]: multilegType,
          [MixpanelEventProperty.Source]: MixpanelEventSource.MultilegDeepDive,
        });
        navigateToMultilegCombo(multilegType, undefined, legMarketAccounts, legMarkets, [security.Symbol]);
      },
      [mixpanel, navigateToMultilegCombo, security, legMarketAccounts, legMarkets]
    );

    const { marketDataStatistics } = useMarketDataStatistics(symbol || '', [], 'MultilegDeepdive');
    const dispatch = useAppStateDispatch();
    const displaySymbol = useAppStateSelector(
      (state: AppState) => selectEditableProperties(state, panelID)?.displaySymbol
    );

    const handleChangeSymbol = useCallback(
      (newSymbol?: string) => {
        if (newSymbol) {
          dispatch(setResolvedSymbol({ resolvedSymbol: newSymbol, panelID }));
        }
      },
      [dispatch, panelID]
    );

    const availableSecurities = useMemo(() => searchableSecurities?.filter(s => isMultileg(s)), [searchableSecurities]);

    const snapshotRequest = useMemo(
      () =>
        symbol
          ? {
              name: MARKET_DATA_SNAPSHOT,
              tag: 'MultilegComboHeader',
              DepthType: DepthTypeEnum.Price,
              Markets: EMPTY_ARRAY,
              Symbol: symbol,
              LiquidityType: showFirmLiquidity ? LiquidityTypeEnum.Firm : LiquidityTypeEnum.Indicative,
              UnifiedLiquidity: 'Disabled',
              Depth: 1,
              FeeMode: showAllInPrices ? FeeModeEnum.Taker : undefined,
            }
          : null,
      [symbol, showAllInPrices, showFirmLiquidity]
    );
    const { data: snapshotsSubscription } = useSubscription<MarketDataSnapshot>(snapshotRequest);
    const snapshotData = useObservableValue(
      () => snapshotsSubscription.pipe(map(snapshot => snapshot.data?.[0])),
      [snapshotsSubscription]
    );

    const isPricingModeBPS = useIsPricingModeBPS(security);
    const { bidPrice, offerPrice } = getMarketDataLevelPrices({
      snapshot: snapshotData,
      index: 0,
      isPricingModeBPS,
    });

    const isOption = useMemo(() => isMultilegOptionComboType(instrumentType), [instrumentType]);

    const greeks = useMemo(() => {
      if (isOption) {
        return (
          <>
            <InfoBox
              label="Delta"
              value={marketDataStatistics?.Symbol === symbol ? marketDataStatistics?.Delta : undefined}
            />
            <InfoBox
              label="Gamma"
              value={marketDataStatistics?.Symbol === symbol ? marketDataStatistics?.Gamma : undefined}
            />
            <InfoBox
              label="Vega"
              value={marketDataStatistics?.Symbol === symbol ? marketDataStatistics?.Vega : undefined}
            />
            <InfoBox
              label="Theta"
              value={marketDataStatistics?.Symbol === symbol ? marketDataStatistics?.Theta : undefined}
            />
          </>
        );
      }
      return null;
    }, [symbol, marketDataStatistics, isOption]);

    // Temporarily hiding until back end ready
    // const highLowVolume = useMemo(() => {
    //   if (
    //     security &&
    //     (instrumentType === MultilegComboType.Delta1Spread ||
    //       instrumentType === MultilegComboType.SyntheticCross)
    //   ) {
    //     return <SparklineSummary security={security} exchangeMarkets={EMPTY_ARRAY} />;
    //   }
    //   return null;
    // }, [instrumentType, security]);
    const dropdownWidth = useSymbolSelectorWidth();

    return (
      <HStack justifyContent="space-between" h={48} flex="0 0 auto">
        <HStack justifyContent="space-between" gap="spacing8" color="colorTextImportant">
          {!isEditing && security && (
            <SymbolSelector
              appearance={SymbolSelectorAppearance.Ghost}
              symbol={security.Symbol}
              availableSecurities={availableSecurities}
              onSymbolChanged={handleChangeSymbol}
              startOpen={false}
              dropdownWidth={dropdownWidth}
              maxHeight={480}
              showGroups
              size={FormControlSizes.Small}
              showFavorites={true}
              dropdownId="deep-dive-symbol-selector-dropdown"
              id="deep-dive-symbol-selector"
            />
          )}
          {isEditing ? (
            <Box w="300px">
              <Input
                value={displaySymbol ?? security?.DisplaySymbol}
                data-testid="multileg-display-symbol-input"
                onChange={e =>
                  isEditing
                    ? dispatch(updateEditableProperty({ key: 'displaySymbol', value: e.target.value, panelID }))
                    : null
                }
                suffix={<Icon icon={IconName.Pencil} />}
              />
            </Box>
          ) : null}
          {/*{highLowVolume}*/}
        </HStack>
        <HStack justifyContent="space-between" gap="spacing8">
          <Divider color={theme.colors.gray['000']} />
          <InfoBox
            label="Bid"
            value={snapshotData?.Symbol === symbol ? bidPrice : undefined}
            isPricingModeBPS={isPricingModeBPS}
          />
          {isOption && (
            <InfoBox
              label="Mark"
              value={marketDataStatistics?.Symbol === symbol ? marketDataStatistics?.MarkPrice : undefined}
            />
          )}
          <InfoBox
            label="Offer"
            value={snapshotData?.Symbol === symbol ? offerPrice : undefined}
            isPricingModeBPS={isPricingModeBPS}
          />
          {greeks}
          <SymbolActionsMenu
            security={security}
            mixpanelSource={MixpanelEventSource.MultilegDeepDive}
            onCreateMultileg={handleCreateMultileg}
            onCloneSymbol={cloneMultilegSymbol}
            dataTestId="multileg-deep-dive-menu"
          />
        </HStack>
      </HStack>
    );
  }
);

const InfoBox = ({
  label,
  value,
  isPricingModeBPS = false,
}: {
  label: string;
  value: string | undefined;
  isPricingModeBPS?: boolean;
}) => {
  return (
    <VStack alignItems="start" ml="spacing24" data-testid={`multileg-header-${label.toLowerCase()}`}>
      <Text size="fontSizeTiny" color="colorTextSubtle" transform="uppercase">
        {label}
      </Text>
      <Text weight="fontWeightMedium" data-testid="24high">
        {value == null ? (
          '--'
        ) : (
          <InlineFormattedNumber number={value} currency={isPricingModeBPS ? 'BPS' : undefined} />
        )}
      </Text>
    </VStack>
  );
};
